import {
  BorrowerContractualizationResponse,
  BorrowerLoanDetailDto,
  UpdateLoanInsuranceCommand
} from 'api/borrower';
import { ReferentialDto } from 'api/referential';
import { FormikProps } from 'formik';
import { useCallback, useEffect, useRef, useState } from 'react';
import { borrowersAPI } from 'services/swagger-api';
import { BorrowerLoanFormValues } from './borrowerLoanForm';

interface ReferentialLabels {
  loanTypes: ReferentialDto[];
  interestRateTypes: ReferentialDto[];
  defermentTypes: ReferentialDto[];
}

interface UseBorrowerLoanPanelProps {
  borrowerId: string;
  onSuccess: (response: BorrowerContractualizationResponse) => void;
  referentials: ReferentialLabels;
}

export const useBorrowerLoanPanel = ({
  borrowerId,
  onSuccess,
  referentials
}: UseBorrowerLoanPanelProps) => {
  const [loans, setLoans] = useState<BorrowerLoanDetailDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<number | string>(0);
  const [isFormValid, setIsFormValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  const formikRef = useRef<FormikProps<BorrowerLoanFormValues>>(null);

  // Fetch loans
  const fetchLoans = useCallback(async () => {
    if (!borrowerId) return;

    setIsLoading(true);
    try {
      const response = await borrowersAPI.getBorrowerLoans({ borrowerId });
      setLoans(response.loans || []);
    } catch (error) {
      console.error('Error fetching borrower loans:', error);
    } finally {
      setIsLoading(false);
    }
  }, [borrowerId]);

  useEffect(() => {
    fetchLoans();
  }, [fetchLoans]);

  // Get current loan based on active tab
  const currentLoan = loans[activeTab as number];

  // Map loan to form values (id -> code)
  const mapLoanToFormValues = useCallback(
    (loan: BorrowerLoanDetailDto): BorrowerLoanFormValues => {
      const loanType = referentials.loanTypes.find(
        (item) => item.id === loan.loanTypeId
      );
      const interestRateType = referentials.interestRateTypes.find(
        (item) => item.id === loan.interestRateTypeId
      );
      const defermentType = referentials.defermentTypes.find(
        (item) => item.id === loan.defermentTypeId
      );

      return {
        id: loan.id,
        loanAmount: loan.loanAmount,
        totalDuration: loan.totalDuration,
        interestRateType: interestRateType?.code || '',
        interestRatePercentage: loan.interestRatePercentage,
        loanType: loanType?.code || '',
        defermentPeriod: loan.defermentPeriod,
        defermentType: defermentType?.code || '',
        insuranceAmount: loan.insuranceAmount ?? undefined
      };
    },
    [referentials]
  );

  // Map form values to update command (code -> id)
  const mapFormValuesToCommand = useCallback(
    (values: BorrowerLoanFormValues): UpdateLoanInsuranceCommand => {
      const loanType = referentials.loanTypes.find(
        (item) => item.code === values.loanType
      );
      const interestRateType = referentials.interestRateTypes.find(
        (item) => item.code === values.interestRateType
      );
      const defermentType = referentials.defermentTypes.find(
        (item) => item.code === values.defermentType
      );

      return {
        borrowerId: borrowerId,
        loanAmount: values.loanAmount,
        totalDuration: values.totalDuration,
        interestRateTypeId: interestRateType?.id || '',
        interestRatePercentage: values.interestRatePercentage,
        loanTypeId: loanType?.id || '',
        defermentPeriod: values.defermentPeriod,
        defermentTypeId: defermentType?.id || '',
        insuranceAmount: values.insuranceAmount ?? 0
      };
    },
    [borrowerId, referentials]
  );

  // Submit update
  const handleSubmit = useCallback(
    async (values: BorrowerLoanFormValues) => {
      if (!currentLoan || !borrowerId) return;

      setIsSubmitting(true);
      try {
        const command = mapFormValuesToCommand(values);
        const response = await borrowersAPI.updateLoanContractualization({
          loanId: currentLoan.id,
          updateLoanInsuranceCommand: command
        });

        // Update global context with new contractualization response
        onSuccess(response);

        // Refresh loans list
        await fetchLoans();

        // Reset dirty state
        setIsDirty(false);
      } catch (error) {
        console.error('Error updating loan:', error);
      } finally {
        setIsSubmitting(false);
      }
    },
    [currentLoan, borrowerId, mapFormValuesToCommand, onSuccess, fetchLoans]
  );

  // Handle tab change
  const handleTabChange = useCallback((value: number | string) => {
    setActiveTab(value);
    setIsDirty(false);
  }, []);

  // Handle validation change
  const handleValidationChange = useCallback((isValid: boolean) => {
    setIsFormValid(isValid);
  }, []);

  // Handle dirty change
  const handleDirtyChange = useCallback((dirty: boolean) => {
    setIsDirty(dirty);
  }, []);

  return {
    loans,
    isLoading,
    activeTab,
    handleTabChange,
    currentLoan,
    isFormValid,
    isSubmitting,
    isDirty,
    formikRef,
    handleSubmit,
    handleValidationChange,
    handleDirtyChange,
    mapLoanToFormValues
  };
};
